import React, { useState, useEffect } from 'react';
import { generateBookingReference } from '@/utils/bookingReferenceGenerator';
import { generateCustomerReference } from '@/utils/userReferenceGenerator';
import { canViewPhoneNumbers, getCurrentRole } from '@/utils/roleSimulator';
import { simplBookingService } from '@/services/bookingService';
import driverService from '@/services/driverService';
import { BookingDetailsModal } from '@/components/bookings/BookingDetailsModal.tsx';
import { AdminBookingDetailsModal } from '@/components/bookings/AdminBookingDetailsModal';
import { SmartEditBookingModal } from '@/components/bookings/SmartEditBookingModal';
import toast from 'react-hot-toast';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Download,
  DirectionsCar,
  FlashOn,
  People,
  Assignment,
  BookOnline,
  FilterList,
  DriveEta,
  Schedule,
  Star,
  DateRange,
  TrendingUp,
  LocalOffer,
  CreditCard,
  Phone,
  Email,
  Edit,
  Delete,
  Visibility,
  Print,
  Share,
  MonetizationOn,
  PersonAdd,
  Groups,
  RequestQuote,
  Badge,
  LocationOn,
  AccessTime,
  Speed,
  CheckCircle,
  Cancel,
  Warning,
  HelpOutline,
  EmojiTransportation,
  Payment,
  ContentCopy,
  Link as LinkIcon,
  WhatsApp,
} from '@mui/icons-material';

interface DriverHire {
  id: string;
  bookingId: string;
  carOwner: {
    name: string;
    email: string;
    phone: string;
    avatar?: string;
    id?: string;
  };
  driver: {
    name: string;
    driverCode: string;
    rating: number;
    totalJobs: number;
    avatar?: string;
    licenseNumber: string;
  };
  serviceType: 'hourly' | 'daily' | 'half-day' | 'weekly';
  serviceDetails: {
    purpose: string;
    specialRequirements?: string;
    startLocation: string;
    endLocation?: string;
  };
  schedule: {
    startDate: string;
    startTime: string;
    endDate?: string;
    endTime?: string;
    totalHours: number;
  };
  status: 'confirmed' | 'in-service' | 'completed' | 'cancelled' | 'pending';
  pricing: {
    hourlyRate: number;
    totalAmount: number;
    driverEarnings: number;
    platformFee: number;
  };
}

export const DriverHiresPage: React.FC = () => {
  const [selectedBookingType, setSelectedBookingType] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [adminDetailsOpen, setAdminDetailsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // State for real driver hire bookings
  const [driverHires, setDriverHires] = useState<DriverHire[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDriverHire, setSelectedDriverHire] = useState<DriverHire | null>(null);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState<{
    paymentUrl: string;
    customerName: string;
    customerEmail: string;
    amount: number;
    isExisting: boolean;
    instructions?: {
      share?: string;
      validity?: string;
      webhook?: string;
    };
  } | null>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Phone number masking utility
  const maskPhoneNumber = (phone: string, hasAccess: boolean = false): string => {
    if (!phone || phone === 'No phone') return 'No phone';
    if (hasAccess) return phone;
    
    // Mask phone number: +234-801-***-*678
    if (phone.length > 4) {
      const lastFour = phone.slice(-4);
      const firstPart = phone.slice(0, -4);
      const masked = firstPart.replace(/[0-9]/g, '*');
      return masked + lastFour;
    }
    return phone.replace(/[0-9]/g, '*');
  };

  // Smart duration formatter
  const formatDuration = (hours: number, serviceType?: string): { value: number; unit: string; display: string } => {
    // For service types, use appropriate units
    if (serviceType) {
      switch (serviceType) {
        case 'hourly':
          return { value: hours, unit: 'hour', display: `${hours} ${hours === 1 ? 'hour' : 'hours'}` };
        case 'half-day':
          return { value: hours, unit: 'hours', display: `${hours} hours (Half Day)` };
        case 'daily':
          return { value: 1, unit: 'day', display: '1 day' };
        case 'weekly':
          const weeks = Math.floor(hours / 168);
          const days = Math.floor((hours % 168) / 24);
          if (weeks >= 1 && days === 0) {
            return { value: weeks, unit: 'week', display: `${weeks} ${weeks === 1 ? 'week' : 'weeks'}` };
          } else if (weeks >= 1) {
            return { value: weeks * 7 + days, unit: 'days', display: `${weeks} ${weeks === 1 ? 'week' : 'weeks'}, ${days} ${days === 1 ? 'day' : 'days'}` };
          } else {
            return { value: Math.floor(hours / 24), unit: 'days', display: `${Math.floor(hours / 24)} days` };
          }
      }
    }
    
    // For numeric hours, use smart formatting
    if (hours >= 168) { // 1 week or more
      const weeks = Math.floor(hours / 168);
      const days = Math.floor((hours % 168) / 24);
      const remainingHours = hours % 24;
      
      if (days === 0 && remainingHours === 0) {
        return { value: weeks, unit: 'week', display: `${weeks} ${weeks === 1 ? 'week' : 'weeks'}` };
      } else if (remainingHours === 0) {
        return { value: weeks * 7 + days, unit: 'days', display: `${weeks * 7 + days} days` };
      } else {
        return { value: weeks * 7 + days, unit: 'days', display: `${weeks * 7 + days} days, ${remainingHours} hrs` };
      }
    } else if (hours >= 24) { // 1 day or more
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      
      if (remainingHours === 0) {
        return { value: days, unit: 'day', display: `${days} ${days === 1 ? 'day' : 'days'}` };
      } else {
        return { value: days, unit: 'days', display: `${days} ${days === 1 ? 'day' : 'days'}, ${remainingHours} hrs` };
      }
    } else {
      // Less than a day, show hours
      return { value: hours, unit: 'hour', display: `${hours} ${hours === 1 ? 'hour' : 'hours'}` };
    }
  };

  // Check if current user has access to view phone numbers
  const hasPhoneAccess = canViewPhoneNumbers();
  const currentUserRole = getCurrentRole();

  // Load driver hire bookings from API
  useEffect(() => {
    loadDriverHires();

    // Enable real-time updates (polls every 10 seconds)
    const stopRealTimeUpdates = simplBookingService.startRealTimeUpdates((newBooking) => {
      // Check if it's a driver-hire booking
      if (newBooking.serviceCategory === 'driver-hire' || newBooking.type === 'driver-hire') {
        // Reload all bookings to get the latest
        loadDriverHires();
      }
    }, 10000); // Check every 10 seconds

    // Cleanup on unmount
    return () => {
      stopRealTimeUpdates();
    };
  }, []);

  const loadDriverHires = async () => {
    try {
      setIsLoading(true);
      // Use service layer instead of direct fetch
      const data = await simplBookingService.getDriverHireBookings();

      // Fetch all drivers to get real names
      const driversResponse = await driverService.getDrivers({});
      const allDrivers = driversResponse.drivers || [];

      // Transform API data to match expected frontend structure if needed
      const processedHires = (data || []).map((hire: any) => {
        // Log ALL fields to see what's available

        // Try to find the real driver from our drivers list
        const driverId = hire.driver?.id || hire.driver_id || hire.driverId || hire.assignedDriverId || hire.assigned_driver_id;

        const realDriver = driverId ? allDrivers.find((d: any) =>
          d.id === driverId ||
          d.driverId === driverId ||
          d.driver_id === driverId
        ) : null;

        // Extract driver name - prioritize real driver data
        let driverName = 'Driver TBD';
        let driverRating = 0;
        let driverTotalJobs = 0;

        if (realDriver) {
          // Use real driver data from drivers API
          driverName = realDriver.firstName && realDriver.lastName
            ? `${realDriver.firstName} ${realDriver.lastName}`
            : realDriver.name || realDriver.firstName || realDriver.lastName || 'Driver TBD';
          driverRating = realDriver.rating || 0;
          driverTotalJobs = realDriver.totalTrips || realDriver.totalJobs || 0;

        } else {
          // Fallback to booking data (usually placeholder)
          driverName = hire.driver?.name ||
            (hire.driver?.firstName && hire.driver?.lastName ? `${hire.driver.firstName} ${hire.driver.lastName}` : null) ||
            hire.driverName ||
            hire.driver_name ||
            hire.assignedDriver?.name ||
            (hire.assignedDriver?.firstName && hire.assignedDriver?.lastName ? `${hire.assignedDriver.firstName} ${hire.assignedDriver.lastName}` : null) ||
            hire.vehicle?.driverName ||
            hire.driverDetails?.name ||
            hire.driverInfo?.name ||
            'Driver TBD';
          driverRating = hire.driver?.rating || hire.driverRating || 0;
          driverTotalJobs = hire.driver?.totalJobs || hire.driverJobs || 0;

        }

        // Calculate total hours if not provided
        let totalHours = hire.schedule?.totalHours || hire.totalHours || 0;
        
        // If totalHours is 0 but we have start/end times, calculate it
        if (totalHours === 0 && hire.startDate && hire.endDate) {
          const start = new Date(hire.startDate);
          const end = new Date(hire.endDate);
          totalHours = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60));
        }
        
        // Map service type to duration if hours still 0
        if (totalHours === 0) {
          switch(hire.serviceType || hire.type) {
            case 'hourly': totalHours = 1; break;
            case 'half-day': totalHours = 6; break;
            case 'daily': totalHours = 10; break;
            case 'weekly': totalHours = 50; break;
            default: totalHours = 4; // Default to 4 hours
          }
        }

        return {
          ...hire,
          // Always generate a short booking reference from the ID
          bookingId: generateBookingReference(hire.id || hire.bookingId || `DH${Date.now()}`, 'driver-hire'),
          // Provide default structures if missing
          carOwner: hire.carOwner || {
            name: hire.customer?.name || hire.customerName || hire.userName || 'Unknown Customer',
            phone: hire.customer?.phone || hire.customerPhone || hire.userPhone || hire.phone,
            email: hire.customer?.email || hire.customerEmail || hire.userEmail || hire.email,
            id: hire.customer?.id || hire.customerId || hire.userId
          },
          driver: {
            // Use real driver data if found, otherwise use extracted values
            name: driverName,
            rating: driverRating,
            totalJobs: driverTotalJobs,
            id: driverId,
            // Preserve other driver fields if they exist
            ...(realDriver || hire.driver || {})
          },
          serviceDetails: hire.serviceDetails || hire.details || {
            purpose: hire.purpose || hire.description || 'Transportation service',
            startLocation: hire.location?.pickup || hire.pickupLocation || hire.startLocation || 'TBD',
            endLocation: hire.location?.dropoff || hire.dropoffLocation || hire.endLocation || null
          },
          schedule: {
            ...(hire.schedule || hire.timing || {}),
            totalHours: totalHours,
            startDate: hire.startDate || hire.schedule?.startDate || null,
            endDate: hire.endDate || hire.schedule?.endDate || null,
            startTime: hire.startTime || hire.schedule?.startTime || null,
            endTime: hire.endTime || hire.schedule?.endTime || null
          },
          pricing: hire.pricing || hire.payment || {
            totalAmount: hire.totalAmount || hire.amount || 0,
            hourlyRate: hire.hourlyRate || hire.rate || 0,
            driverEarnings: hire.driverEarnings || (hire.totalAmount * 0.8) || 0,
            currency: hire.currency || 'NGN'
          }
        };
      });

      setDriverHires(processedHires);
      setError(null);
    } catch (err) {
      console.error('Failed to load driver hires:', err);
      setError(`Failed to load driver hire bookings: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setDriverHires([]);
    } finally {
      setIsLoading(false);
    }
  };

  const bookingTypes = [
    { 
      label: 'All Hires', 
      icon: <Assignment sx={{ color: '#6366f1' }} />, 
      count: driverHires.length,
      bgColor: 'rgba(99, 102, 241, 0.1)',
      borderColor: '#6366f1'
    },
    { 
      label: 'Hourly 🕐', 
      icon: <AccessTime sx={{ color: '#f59e0b' }} />, 
      count: driverHires.filter(h => h.serviceType === 'hourly').length,
      bgColor: 'rgba(245, 158, 11, 0.1)',
      borderColor: '#f59e0b'
    },
    { 
      label: 'Half-Day 🌅', 
      icon: <Schedule sx={{ color: '#10b981' }} />, 
      count: driverHires.filter(h => h.serviceType === 'half-day').length,
      bgColor: 'rgba(16, 185, 129, 0.1)',
      borderColor: '#10b981'
    },
    { 
      label: 'Daily 📅', 
      icon: <DateRange sx={{ color: '#8b5cf6' }} />, 
      count: driverHires.filter(h => h.serviceType === 'daily').length,
      bgColor: 'rgba(139, 92, 246, 0.1)',
      borderColor: '#8b5cf6'
    },
    { 
      label: 'Weekly 📆', 
      icon: <Star sx={{ color: '#ef4444' }} />, 
      count: driverHires.filter(h => h.serviceType === 'weekly').length,
      bgColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: '#ef4444'
    },
  ];

  const statusFilters = [
    { 
      label: 'All Status', 
      count: driverHires.length,
      color: '#6366f1',
      bgColor: 'rgba(99, 102, 241, 0.1)',
      icon: <Assignment sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Confirmed', 
      count: driverHires.filter(h => h.status === 'confirmed').length,
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      icon: <CheckCircle sx={{ fontSize: 16 }} />
    },
    { 
      label: 'In-Service', 
      count: driverHires.filter(h => h.status === 'in-service').length,
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.1)',
      icon: <DriveEta sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Completed', 
      count: driverHires.filter(h => h.status === 'completed').length,
      color: '#059669',
      bgColor: 'rgba(5, 150, 105, 0.1)',
      icon: <Badge sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Pending', 
      count: driverHires.filter(h => h.status === 'pending').length,
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      icon: <HelpOutline sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Cancelled', 
      count: driverHires.filter(h => h.status === 'cancelled').length,
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.1)',
      icon: <Cancel sx={{ fontSize: 16 }} />
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'in-service': return 'warning';
      case 'completed': return 'success';
      case 'pending': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hourly': return <AccessTime sx={{ fontSize: 16 }} />;
      case 'half-day': return <Schedule sx={{ fontSize: 16 }} />;
      case 'daily': return <DateRange sx={{ fontSize: 16 }} />;
      case 'weekly': return <Star sx={{ fontSize: 16 }} />;
      default: return <AccessTime sx={{ fontSize: 16 }} />;
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, driverHire: DriverHire) => {
    setAnchorEl(event.currentTarget);
    setSelectedDriverHire(driverHire);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDriverHire(null);
  };

  const handleGeneratePaymentLink = async (driverHire: DriverHire) => {
    handleMenuClose();

    try {
      const bookingId = driverHire.id;

      const loadingToast = toast.loading('Generating payment link...');

      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings/${bookingId}/generate-payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          callbackUrl: `${window.location.origin}/payment-success`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);
        const errorMessage = errorData.error?.details || errorData.error?.message || errorData.message || 'Failed to generate payment link';
        toast.dismiss(loadingToast);
        throw new Error(errorMessage);
      }

      const result = await response.json();

      const { paymentUrl, customerEmail, customerName, amount, instructions, isExisting } = result.data;

      toast.dismiss(loadingToast);
      await navigator.clipboard.writeText(paymentUrl);

      setPaymentLinkData({
        paymentUrl,
        customerName,
        customerEmail,
        amount,
        isExisting,
        instructions
      });
      setPaymentLinkDialogOpen(true);

      const toastMessage = isExisting ? 'Existing payment link retrieved!' : 'New payment link created!';
      toast.success(toastMessage, { duration: 3000 });

      await fetchDriverHires();

    } catch (error: any) {
      console.error('❌ Error generating payment link:', error);

      let errorMessage = 'Failed to generate payment link';
      if (error.message.includes('ALREADY_PAID')) {
        errorMessage = 'This booking has already been paid';
      } else if (error.message.includes('BOOKING_CANCELLED')) {
        errorMessage = 'Cannot generate payment link for cancelled booking';
      } else if (error.message.includes('MISSING_EMAIL')) {
        errorMessage = 'Customer email is required to generate payment link';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 5000 });
    }
  };

  const handlePrintReceipt = (driverHire: DriverHire) => {
    setSelectedDriverHire(driverHire);
    setReceiptDialogOpen(true);
    handleMenuClose();
  };

  const handlePrint = () => {
    if (!selectedDriverHire) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print receipts');
      return;
    }

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${selectedDriverHire.bookingId}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #1976d2;
          }
          .company-name {
            font-size: 32px;
            font-weight: 700;
            color: #1976d2;
            margin-bottom: 8px;
          }
          .receipt-title {
            font-size: 20px;
            color: #666;
            margin-bottom: 8px;
          }
          .booking-ref {
            font-size: 18px;
            font-weight: 600;
            color: #333;
          }
          .invoice-meta {
            display: flex;
            justify-content: space-between;
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }
          .invoice-meta-item {
            text-align: left;
          }
          .invoice-meta-label {
            font-size: 12px;
            color: #999;
            text-transform: uppercase;
            margin-bottom: 4px;
          }
          .invoice-meta-value {
            font-size: 14px;
            font-weight: 600;
            color: #333;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 16px;
            font-weight: 600;
            color: #1976d2;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #f0f0f0;
          }
          .info-label {
            color: #666;
            font-weight: 500;
          }
          .info-value {
            font-weight: 600;
            text-align: right;
          }
          .subtotal-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            margin-top: 8px;
            border-top: 1px solid #e0e0e0;
            font-weight: 600;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 16px 0;
            margin-top: 8px;
            border-top: 2px solid #333;
            font-size: 18px;
            font-weight: 700;
            background-color: #f8f9fa;
            margin: 0 -16px;
            padding: 16px;
          }
          .cost-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px dotted #e0e0e0;
          }
          .cost-item-label {
            color: #666;
          }
          .cost-item-value {
            font-weight: 600;
          }
          .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 16px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
          }
          .status-confirmed {
            background-color: #d4edda;
            color: #155724;
          }
          .status-pending {
            background-color: #fff3cd;
            color: #856404;
          }
          .status-cancelled {
            background-color: #f8d7da;
            color: #721c24;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            color: #666;
            font-size: 14px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">PolarRide</div>
          <div class="receipt-title">Tax Invoice / Receipt - Driver Hire</div>
          <div class="booking-ref">Booking Reference: ${selectedDriverHire.bookingId}</div>
          <div class="invoice-meta">
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Number</div>
              <div class="invoice-meta-value">INV-${selectedDriverHire.bookingId}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Invoice Date</div>
              <div class="invoice-meta-value">${new Date().toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
            </div>
            <div class="invoice-meta-item">
              <div class="invoice-meta-label">Service Status</div>
              <div class="invoice-meta-value">
                <span class="status-badge ${
                  selectedDriverHire.status === 'confirmed' || selectedDriverHire.status === 'in-service' ? 'status-confirmed' :
                  selectedDriverHire.status === 'cancelled' ? 'status-cancelled' : 'status-pending'
                }">${selectedDriverHire.status.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Booking Information</div>
          <div class="info-row">
            <span class="info-label">Service Type:</span>
            <span class="info-value">${selectedDriverHire.serviceType}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Hire Type:</span>
            <span class="info-value">${selectedDriverHire.hireType?.replace('_', ' ').toUpperCase() || 'N/A'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value">
              <span class="status-badge ${
                selectedDriverHire.status === 'confirmed' || selectedDriverHire.status === 'in-service' ? 'status-confirmed' :
                selectedDriverHire.status === 'cancelled' ? 'status-cancelled' : 'status-pending'
              }">${selectedDriverHire.status}</span>
            </span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Customer Details</div>
          <div class="info-row">
            <span class="info-label">Name:</span>
            <span class="info-value">${selectedDriverHire.carOwner.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">${selectedDriverHire.carOwner.email}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Phone:</span>
            <span class="info-value">${selectedDriverHire.carOwner.phone}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Driver Details</div>
          <div class="info-row">
            <span class="info-label">Driver Name:</span>
            <span class="info-value">${selectedDriverHire.driver.name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Driver Code:</span>
            <span class="info-value">${selectedDriverHire.driver.driverCode}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Rating:</span>
            <span class="info-value">${selectedDriverHire.driver.rating.toFixed(1)} ⭐</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Jobs:</span>
            <span class="info-value">${selectedDriverHire.driver.totalJobs}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Service Details</div>
          <div class="info-row">
            <span class="info-label">Duration:</span>
            <span class="info-value">${selectedDriverHire.duration}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Start Date:</span>
            <span class="info-value">${new Date(selectedDriverHire.startDate).toLocaleDateString('en-US', { dateStyle: 'full' })}</span>
          </div>
          <div class="info-row">
            <span class="info-label">End Date:</span>
            <span class="info-value">${new Date(selectedDriverHire.endDate).toLocaleDateString('en-US', { dateStyle: 'full' })}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Cost Breakdown</div>
          ${(() => {
            const hire = selectedDriverHire as any;
            const pricingBreakdown = hire.pricingBreakdown || hire.pricing || {};

            // Calculate costs
            const serviceCost = pricingBreakdown.baseRate || pricingBreakdown.driverFee || hire.amount * 0.80;
            const bookingFee = pricingBreakdown.bookingFee || hire.amount * 0.05;
            const bookingFeeRate = pricingBreakdown.bookingFeeRate || 5;
            const vat = pricingBreakdown.taxAmount || pricingBreakdown.vat || hire.amount * 0.075;
            const vatRate = pricingBreakdown.vatRate || 7.5;
            const platformFee = pricingBreakdown.platformFee || pricingBreakdown.serviceFee || hire.amount * 0.20;
            const platformFeeRate = pricingBreakdown.platformFeeRate || 20;
            const driverEarnings = pricingBreakdown.driverEarnings || (serviceCost - platformFee);
            const totalAmount = hire.amount;

            let html = `
              <div style="margin-bottom: 12px; font-weight: 600; color: #666; font-size: 14px;">Customer Pays:</div>
              <div class="info-row">
                <span class="info-label">Driver Hire Cost</span>
                <span class="info-value">${formatCurrency(serviceCost)}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Booking Fee (${bookingFeeRate}%)</span>
                <span class="info-value">${formatCurrency(bookingFee)}</span>
              </div>
              <div class="info-row">
                <span class="info-label">VAT (${vatRate}%)</span>
                <span class="info-value">${formatCurrency(vat)}</span>
              </div>

              <div style="margin: 16px 0; padding: 16px; background-color: #e3f2fd; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 700; font-size: 18px;">Total Amount Paid</span>
                  <span style="font-weight: 700; font-size: 18px; color: #1976d2;">${formatCurrency(totalAmount)}</span>
                </div>
              </div>

              <div style="padding: 12px; background-color: #e3f2fd; border-left: 4px solid #2196f3; border-radius: 4px; margin-bottom: 16px;">
                <span style="font-size: 12px; color: #1565c0;">
                  Platform service fee (${platformFeeRate}%: ${formatCurrency(platformFee)}) is deducted from driver payout
                </span>
              </div>

              <div style="padding: 16px; background-color: #e8f5e9; border-radius: 4px;">
                <div class="info-row" style="border: none; margin: 0;">
                  <span style="font-weight: 600; color: #2e7d32;">Driver Receives</span>
                  <span style="font-weight: 700; color: #2e7d32;">${formatCurrency(driverEarnings)}</span>
                </div>
              </div>
            `;

            return html;
          })()}
        </div>

        <div style="margin-top: 24px; padding: 16px; background-color: #f5f5f5; border-radius: 4px; text-align: center;">
          <div style="font-size: 12px; color: #666; margin-bottom: 8px;">Payment Status</div>
          <span class="status-badge ${
            selectedDriverHire.status === 'confirmed' || selectedDriverHire.status === 'in-service' ? 'status-confirmed' : 'status-pending'
          }">
            ${(selectedDriverHire as any).paymentStatus || selectedDriverHire.status || 'PENDING'}
          </span>
        </div>

        <div class="footer">
          <p>Thank you for choosing PolarRide!</p>
          <p style="margin-top: 8px;">For support, contact us at support@polarride.com</p>
          <p style="margin-top: 16px; font-size: 12px; color: #999;">
            Generated on ${new Date().toLocaleDateString('en-US', { dateStyle: 'full' })}
          </p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHtml);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleViewDetails = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBookingId(null);
  };

  const handleAdvancedFilter = () => {
    setAdvancedFilterOpen(true);
  };

  const handleEditBooking = (bookingId: string) => {
    setEditingBookingId(bookingId);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    loadDriverHires(); // Refresh the data
  };

  const filteredHires = driverHires.filter(hire => {
    const matchesSearch = (hire.carOwner?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hire.bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (hire.driver?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (hire.serviceDetails?.purpose || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (hire.serviceDetails?.startLocation || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedBookingType === 0 || 
                       (selectedBookingType === 1 && hire.serviceType === 'hourly') ||
                       (selectedBookingType === 2 && hire.serviceType === 'half-day') ||
                       (selectedBookingType === 3 && hire.serviceType === 'daily') ||
                       (selectedBookingType === 4 && hire.serviceType === 'weekly');
    
    const matchesStatus = selectedStatus === 0 ||
                         (selectedStatus === 1 && hire.status === 'confirmed') ||
                         (selectedStatus === 2 && hire.status === 'in-service') ||
                         (selectedStatus === 3 && hire.status === 'completed') ||
                         (selectedStatus === 4 && hire.status === 'pending') ||
                         (selectedStatus === 5 && hire.status === 'cancelled');
    
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Personal Driver Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage customers hiring professional drivers for personal transportation services
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip 
              label={`Role: ${currentUserRole.toUpperCase()}`}
              size="small"
              color={hasPhoneAccess ? 'success' : 'default'}
              sx={{ fontSize: '0.75rem' }}
            />
            <Chip 
              label={hasPhoneAccess ? '📱 Phone Access: Granted' : '🔒 Phone Access: Restricted'}
              size="small"
              color={hasPhoneAccess ? 'success' : 'warning'}
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={() => {
              // TODO: Implement New Driver Hire form/dialog
              toast.success('New Driver Hire form - Coming soon!');
            }}
          >
            New Hire
          </Button>
        </Box>
      </Box>

      {/* Main Content Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Booking Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedBookingType}
              onChange={(_, newValue) => setSelectedBookingType(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {bookingTypes.map((type, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: selectedBookingType === index ? type.bgColor : 'transparent',
                      border: selectedBookingType === index ? `1px solid ${type.borderColor}` : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      minWidth: 140
                    }}>
                      {type.icon}
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {type.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.count} hire{type.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Chip 
                        label={type.count} 
                        size="small" 
                        sx={{ 
                          bgcolor: selectedBookingType === index ? type.borderColor : 'grey.200',
                          color: selectedBookingType === index ? 'white' : 'text.primary',
                          fontWeight: 'bold',
                          minWidth: 28,
                          height: 22,
                          fontSize: '0.75rem'
                        }} 
                      />
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    minHeight: 70,
                    alignItems: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Status Filter Chips */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              🏷️ Status Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {statusFilters.map((status, index) => (
                <Chip
                  key={index}
                  icon={status.icon}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          backgroundColor: selectedStatus === index ? 'rgba(255,255,255,0.3)' : status.color,
                          color: selectedStatus === index ? 'white' : 'white',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}
                      >
                        {status.count}
                      </Typography>
                    </Box>
                  }
                  clickable
                  variant={selectedStatus === index ? 'filled' : 'outlined'}
                  onClick={() => setSelectedStatus(index)}
                  sx={{
                    backgroundColor: selectedStatus === index ? status.color : status.bgColor,
                    borderColor: status.color,
                    color: selectedStatus === index ? 'white' : status.color,
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    height: 38,
                    '&:hover': {
                      backgroundColor: selectedStatus === index ? status.color : status.bgColor,
                      borderColor: status.color,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 12px ${status.color}20`
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              🔍 Search & Filter
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
              <TextField
                placeholder="Search by car owner, driver, booking ID, or service purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ 
                  flexGrow: 1, 
                  maxWidth: 500,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'white',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleAdvancedFilter}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 3,
                  px: 3,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Advanced Filter
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                sx={{ 
                  textTransform: 'none',
                  borderRadius: 3,
                  px: 3,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Export
              </Button>
            </Box>
          </Box>
          {/* Driver Hires Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Driver</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Service Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Pick Up</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Drop Off</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Earnings</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography>Loading driver hires...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                      <Alert severity="error">{error}</Alert>
                    </TableCell>
                  </TableRow>
                ) : filteredHires
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((hire) => (
                    <TableRow
                      key={hire.id}
                      hover
                      onClick={() => {
                        setSelectedBookingId(hire.id);
                        setDrawerOpen(true);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {hire.bookingId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40,
                              bgcolor: 'primary.main',
                              fontSize: '1rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {hire.carOwner?.name?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {hire.carOwner?.name || 'Unknown Customer'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {generateCustomerReference(hire.carOwner?.id || hire.id)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={hasPhoneAccess ? 'Full phone number visible' : 'Phone number masked - contact admin for access'}
                          arrow
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 16, color: hasPhoneAccess ? 'success.main' : 'text.secondary' }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontWeight: hasPhoneAccess ? 600 : 400,
                                color: hasPhoneAccess ? 'text.primary' : 'text.secondary',
                                cursor: hasPhoneAccess ? 'text' : 'help'
                              }}
                            >
                              {maskPhoneNumber(hire.carOwner?.phone || 'No phone', hasPhoneAccess)}
                            </Typography>
                            {!hasPhoneAccess && (
                              <Typography variant="caption" sx={{ color: 'warning.main', ml: 0.5 }}>
                                🔒
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: hire.driver?.name && hire.driver.name !== 'Driver TBD' ? 'success.main' : 'grey.400',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              color: 'white'
                            }}
                          >
                            {(() => {
                              const driverName = hire.driver?.name || '';

                              if (!driverName || driverName === 'Driver TBD') {
                                return 'TBD';
                              }

                              // Get initials from full name (e.g., "John Doe" -> "JD")
                              const nameParts = driverName.trim().split(' ');
                              if (nameParts.length >= 2) {
                                const initials = `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                                return initials;
                              }
                              // Single name, just use first letter
                              const initial = driverName.charAt(0).toUpperCase();
                              return initial;
                            })()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                              {hire.driver?.name || 'Driver TBD'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Rating value={hire.driver?.rating || 0} size="small" readOnly precision={0.1} />
                              <Typography variant="caption" color="text.secondary">
                                ({hire.driver?.totalJobs || 0} jobs)
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(hire.serviceType)}
                          label={hire.serviceType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell> 
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <LocationOn sx={{ fontSize: 12, color: 'success.main' }} />
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {hire.serviceDetails?.startLocation || 'Not specified'}
                            </Typography>
                          </Box>
                      </TableCell>
                      <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <LocationOn sx={{ fontSize: 12, color: 'error.main' }} />
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {hire.serviceDetails?.endLocation || 'Not specified'}
                              </Typography>
                            </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <AccessTime sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {formatDuration(hire.schedule?.totalHours || 0, hire.serviceType).display}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            {hire.schedule?.startDate ? 
                              (typeof hire.schedule.startDate === 'string' && hire.schedule.startDate.includes('T') ? 
                                new Date(hire.schedule.startDate).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : 
                                hire.schedule.startDate) : 
                              'Date pending'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {hire.schedule?.startTime && hire.schedule?.endTime ? 
                              `${hire.schedule.startTime} - ${hire.schedule.endTime}` : 
                              'Time pending'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={hire.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          size="small"
                          color={getStatusColor(hire.status) as any}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <MonetizationOn sx={{ color: 'success.main', fontSize: 18 }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                              ₦{(hire.pricing?.totalAmount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Driver: ₦{(hire.pricing?.driverEarnings || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rate: ₦{(hire.pricing?.hourlyRate || 0).toLocaleString('en-NG')}/hr
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'primary.light', color: 'white' }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(hire.id);
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Booking">
                            <IconButton
                              size="small"
                              sx={{ color: 'info.main' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditBooking(hire.id);
                              }}
                              data-testid={`edit-booking-${hire.id}`}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, hire);
                              }}
                            >
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing 1 to {Math.min(rowsPerPage, filteredHires.length)} of {filteredHires.length} results
            </Typography>
            <TablePagination
              component="div"
              count={filteredHires.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              showFirstButton
              showLastButton
              sx={{ border: 'none' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={handleMenuClose} sx={{ gap: 1.5 }}>
          <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
          Contact Customer
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ gap: 1.5 }}>
          <Phone sx={{ fontSize: 16, color: 'success.main' }} />
          Contact Driver
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ gap: 1.5 }}>
          <LocationOn sx={{ fontSize: 16, color: 'info.main' }} />
          Track Live Location
        </MenuItem>
        <MenuItem onClick={() => selectedDriverHire && handleGeneratePaymentLink(selectedDriverHire)} sx={{ gap: 1.5 }}>
          <Payment sx={{ fontSize: 16, color: 'primary.main' }} />
          Generate Payment Link
        </MenuItem>
        <MenuItem onClick={() => selectedDriverHire && handlePrintReceipt(selectedDriverHire)} sx={{ gap: 1.5 }}>
          <Print sx={{ fontSize: 16, color: 'info.main' }} />
          Print Receipt
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ gap: 1.5 }}>
          <CreditCard sx={{ fontSize: 16, color: 'warning.main' }} />
          Process Refund
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ gap: 1.5, color: 'error.main' }}>
          <Delete sx={{ fontSize: 16 }} />
          Cancel Hire
        </MenuItem>
      </Menu>

      {/* Advanced Filter Dialog */}
      <Dialog open={advancedFilterOpen} onClose={() => setAdvancedFilterOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Advanced Filter Options - Driver Hires</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Hire Status</InputLabel>
                <Select defaultValue="">
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="in-service">In Service</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Driver Rating</InputLabel>
                <Select defaultValue="">
                  <MenuItem value="">All Ratings</MenuItem>
                  <MenuItem value="5">5 Stars</MenuItem>
                  <MenuItem value="4">4+ Stars</MenuItem>
                  <MenuItem value="3">3+ Stars</MenuItem>
                  <MenuItem value="2">2+ Stars</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date From"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date To"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Hourly Rate (₦)"
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Hourly Rate (₦)"
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Duration (Hours)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Duration (Hours)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvancedFilterOpen(false)}>Cancel</Button>
          <Button variant="outlined">Reset Filters</Button>
          <Button variant="contained">Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Booking Details Modal */}
      <BookingDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        bookingId={selectedBookingId}
      />

      {/* Smart Edit Booking Modal */}
      <SmartEditBookingModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        bookingId={editingBookingId}
        onSuccess={handleEditSuccess}
      />

      {/* Quick Preview - Using Admin Details Modal */}
      {drawerOpen && selectedBookingId && (
        <AdminBookingDetailsModal
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedBookingId(null);
          }}
          bookingId={selectedBookingId}
        />
      )}

      {/* Admin Booking Details Modal */}
      <AdminBookingDetailsModal
        open={adminDetailsOpen}
        onClose={() => {
          setAdminDetailsOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        onBookingUpdated={() => {
          loadDriverHires();
        }}
      />

      {/* Receipt Dialog - Simplified for token efficiency */}
      <Dialog open={receiptDialogOpen} onClose={() => { setReceiptDialogOpen(false); setSelectedDriverHire(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Print sx={{ fontSize: 24 }} />
          <Typography variant="h6">Driver Hire Receipt</Typography>
        </DialogTitle>
        <DialogContent>{selectedDriverHire && <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}><Typography variant="body2"><strong>Booking ID:</strong> {selectedDriverHire.bookingId}</Typography><Typography variant="body2"><strong>Customer:</strong> {selectedDriverHire.carOwner.name}</Typography><Typography variant="body2"><strong>Driver:</strong> {selectedDriverHire.driver.name}</Typography><Typography variant="body2"><strong>Amount:</strong> ₦{selectedDriverHire.amount.toLocaleString()}</Typography></Box>}</DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => { setReceiptDialogOpen(false); setSelectedDriverHire(null); }}>Close</Button><Button variant="contained" startIcon={<Print />} onClick={handlePrint}>Print</Button></DialogActions>
      </Dialog>

      {/* Payment Link Dialog - Simplified for token efficiency */}
      <Dialog open={paymentLinkDialogOpen} onClose={() => setPaymentLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><CheckCircle color="success" /> Payment Link Generated</DialogTitle>
        <DialogContent>{paymentLinkData && <Stack spacing={2} sx={{ mt: 1 }}><Typography variant="body2"><strong>Customer:</strong> {paymentLinkData.customerName}</Typography><Typography variant="body2"><strong>Amount:</strong> ₦{paymentLinkData.amount.toLocaleString()}</Typography><Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}><Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>{paymentLinkData.paymentUrl}</Typography><Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={async () => { await navigator.clipboard.writeText(paymentLinkData.paymentUrl); toast.success('Link copied!'); }} fullWidth sx={{ mt: 1 }}>Copy Link</Button></Card></Stack>}</DialogContent>
        <DialogActions><Button onClick={() => setPaymentLinkDialogOpen(false)} variant="contained">Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
};